/**
 * The coordinator in PYP mode: A.5a evidence breadth per homeroom, and the class view of a PYP
 * homeroom (B-1 for coordinators: a PYP class shows breadth, portfolios and narratives — no bands,
 * no percentages of attainment, no assessments).
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '../../lib/useQuery';
import { useSession } from '../../lib/session';
import { Btn, DesignShell, Empty, ErrorNote, S, Skeleton, Table, Td } from '../../design/kit';
import { ELEMENTS, PypBanner } from './ui';

type Col = (typeof ELEMENTS)[number] | 'Learner Profile';
const COLS: Col[] = [...ELEMENTS, 'Learner Profile'];

interface BreadthRow { cohortId: string; name: string; grade: string; teacher: string; total: number; shares: Record<Col, number> }
interface BreadthPayload { inScope: boolean; cuts: { thin: number; strong: number }; rows: BreadthRow[]; overall: { total: number; shares: Record<Col, number> } | null }

/** A-5: under 35% thin, 70% and over strong. The number is always printed; colour is never the only signal. */
function Heat({ pct, cuts }: { pct: number; cuts: BreadthPayload['cuts'] }) {
  const tone = pct < cuts.thin ? 'thin' : pct >= cuts.strong ? 'strong' : 'mid';
  const bg = tone === 'thin' ? 'var(--c-bad-wash)' : tone === 'strong' ? 'var(--c-good-wash)' : 'var(--c-warn-wash)';
  const fg = tone === 'thin' ? 'var(--c-bad)' : tone === 'strong' ? 'var(--c-good)' : 'var(--c-warn-ink)';
  return (
    <span title={tone === 'thin' ? 'Thin' : tone === 'strong' ? 'Strong' : 'Partial'} style={{ display: 'inline-block', minWidth: '52px', textAlign: 'center', padding: '6px 8px', borderRadius: '6px', background: bg, color: fg, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
      {pct}%
    </span>
  );
}

export function PypBreadth() {
  const { school, user } = useSession();
  const q = useQuery<BreadthPayload>('/pyp/breadth');
  const d = q.data;
  const thinnest = d?.overall ? (COLS.slice(0, 5) as Col[]).sort((a, b) => d.overall!.shares[a] - d.overall!.shares[b])[0] : undefined;
  const thinEverywhere = d && thinnest ? d.rows.length > 0 && d.rows.every((r) => r.shares[thinnest] < d.cuts.thin) : false;

  return (
    <DesignShell
      brand={school?.name}
      footer={{ name: user?.name ?? '' }}
      title="Is evidence being collected across all five elements?"
      crumbs={[{ label: 'Coverage', to: '/admin/coverage' }, { label: 'PYP · evidence breadth' }]}
      meta={<span>Share of each homeroom’s evidence tagged to each element and to at least one Learner Profile attribute. Knowledge alone is not coverage.</span>}
      actions={<Btn kind="secondarySm" to="/admin/coverage">DP and MYP coverage</Btn>}
    >
      {q.error ? <ErrorNote error={q.error} retry={q.reload} /> : null}
      {!d ? (q.error ? null : <Skeleton variant="detail" />) : !d.inScope ? (
        <div style={S.card}><Empty title="Outside your programmes" body="PYP evidence breadth is for the PYP coordinator and school leaders." /></div>
      ) : d.rows.length === 0 ? (
        <div style={S.card}><Empty title="No PYP homerooms" body="Evidence breadth appears once the school runs PYP homerooms." /></div>
      ) : (
        <>
          <Table minWidth={760} columns={[{ label: 'Homeroom' }, ...COLS.map((c) => ({ label: c, width: '104px', align: 'right' as const }))]}>
            {d.rows.map((r) => (
              <tr key={r.cohortId}>
                <Td>
                  <Link to={`/admin/classes/${r.cohortId}`} style={{ ...S.link, color: 'var(--c-ink)' }}>{r.name}</Link>
                  <span style={{ ...S.mutedXs, display: 'block' }}>{r.teacher || 'no teacher'} · {r.total} piece{r.total === 1 ? '' : 's'} of evidence</span>
                </Td>
                {COLS.map((c) => <Td key={c} align="right">{r.total ? <Heat pct={r.shares[c]} cuts={d.cuts} /> : <span style={S.mutedXs}>—</span>}</Td>)}
              </tr>
            ))}
          </Table>
          <div className="dashgrid">
            <section aria-label="Where breadth is thin" style={{ ...S.card, display: 'grid', gap: '10px' }}>
              <h2 style={{ ...S.h3, margin: 0 }}>Where breadth is thin</h2>
              {d.overall ? COLS.map((c) => (
                <div key={c} style={{ display: 'grid', gridTemplateColumns: '120px minmax(0, 1fr) 44px', alignItems: 'center', gap: '10px' }}>
                  <span style={S.small}>{c}</span>
                  <span aria-hidden="true" style={{ position: 'relative', height: '10px', borderRadius: '5px', background: 'var(--c-surface-3)' }}>
                    <span style={{ position: 'absolute', inset: 0, width: `${d.overall!.shares[c]}%`, borderRadius: '5px', background: d.overall!.shares[c] < d.cuts.thin ? 'var(--c-bad)' : 'var(--c-brand)' }} />
                  </span>
                  <span style={{ ...S.small, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{d.overall!.shares[c]}%</span>
                </div>
              )) : null}
            </section>
            <section aria-label="Reading this" style={{ ...S.card, display: 'grid', gap: '8px' }}>
              <h2 style={{ ...S.h3, margin: 0 }}>Reading this</h2>
              {thinnest && thinEverywhere ? (
                <p style={{ ...S.small, margin: 0 }}>{thinnest} is under {d.cuts.thin}% in every homeroom. That usually means it is happening but not being captured — a tagging habit, not a teaching gap.</p>
              ) : thinnest ? (
                <p style={{ ...S.small, margin: 0 }}>{thinnest} is the thinnest element across the school this term.</p>
              ) : null}
              <p style={{ ...S.mutedXs, margin: 0 }}>Cells are shares of each homeroom’s own evidence, so a homeroom with less evidence overall is not penalised. Under {d.cuts.thin}% reads thin; {d.cuts.strong}% and over reads strong.</p>
            </section>
          </div>
        </>
      )}
    </DesignShell>
  );
}

/** The coordinator's class page for a PYP homeroom: breadth, and doors into units, portfolios and narratives. */
export function PypClassView({ cohortId, name, meta }: { cohortId: string; name: string; meta: ReactNode }) {
  const { school, user } = useSession();
  const q = useQuery<BreadthPayload>('/pyp/breadth');
  const reports = useQuery<{ drafted: number; approved: number; total: number }>(`/pyp/classes/${cohortId}/reports`);
  const row = q.data?.rows.find((r) => r.cohortId === cohortId);
  const go = (path: string) => `${path}?cohort=${encodeURIComponent(cohortId)}`;
  return (
    <DesignShell brand={school?.name} footer={{ name: user?.name ?? '' }} title={name} crumbs={[{ label: 'Classes', to: '/admin/classes' }, { label: name }]} meta={meta} curriculumKey="ib_pyp">
      <PypBanner />
      {q.error ? <ErrorNote error={q.error} retry={q.reload} /> : null}
      <section aria-label="Evidence breadth" style={{ ...S.card, display: 'grid', gap: '10px' }}>
        <h2 style={{ ...S.h3, margin: 0 }}>Evidence breadth</h2>
        {!q.data ? <Skeleton variant="detail" /> : !row || !row.total ? <Empty title="No tagged evidence yet" /> : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {COLS.map((c) => (
              <div key={c} style={{ display: 'grid', gap: '4px', justifyItems: 'start' }}>
                <span style={S.mutedXs}>{c}</span>
                <Heat pct={row.shares[c]} cuts={q.data!.cuts} />
              </div>
            ))}
          </div>
        )}
        {row ? <span style={S.mutedXs}>{row.total} pieces of evidence · {row.teacher}</span> : null}
      </section>
      <section aria-label="Narrative reports" style={{ ...S.card, ...S.between, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ ...S.h3, margin: 0 }}>Narrative reports</h2>
          <div style={S.sub}>{reports.data ? `${reports.data.drafted} of ${reports.data.total} drafted · ${reports.data.approved} approved` : '…'}</div>
        </div>
        <span style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <Btn kind="secondarySm" to={go('/teacher/pyp/units')}>Units of Inquiry</Btn>
          <Btn kind="secondarySm" to={go('/teacher/pyp/portfolio')}>Portfolios</Btn>
          <Btn kind="primarySm" to={go('/teacher/pyp/reports')}>Narrative reports</Btn>
        </span>
      </section>
    </DesignShell>
  );
}
